import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  ButtonGroup,
  Button,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import { Insights, Schedule } from "@mui/icons-material";
import Calendar from "../Calendar";
import api from "../../utils/api";

const panelCardSx = {
  borderRadius: "18px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
  backgroundColor: "rgba(255,255,255,0.96)",
  backdropFilter: "blur(6px)",
  border: "1px solid rgba(47,99,173,0.08)",
  height: "100%",
  width: "100%",
};

const sectionTitleSx = {
  color: "#223142",
  fontWeight: 700,
  letterSpacing: "0.2px",
};

const buttonSx = (active) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8rem",
  px: 1.5,
  py: 0.65,
  minWidth: 0,
  whiteSpace: "nowrap",
  borderRadius: "20px !important",
  border: "none",
  boxShadow: "none",
  backgroundColor: active ? "#2f63ad" : "rgba(47,99,173,0.08)",
  color: active ? "#fff" : "#2f63ad",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: active ? "#1f4f94" : "rgba(47,99,173,0.18)",
    boxShadow: "none",
    border: "none",
  },
});

const InsightsWithCalendar = () => {
  const [selectedWidget, setSelectedWidget] = useState("subtypes");
  const [insights, setInsights] = useState(null);
  const [subtypeMap, setSubtypeMap] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const [insightsRes, subtypesRes] = await Promise.all([
        api.get("/workorder-insights"),
        api.get("/workorder-subtypes"),
      ]);

      setInsights(insightsRes.data);

      const map = {};
      subtypesRes.data.forEach((s) => {
        map[s.id] = {
          name: s.subtype_name,
          type: s.type,
        };
      });
      setSubtypeMap(map);
    } catch (err) {
      console.error("Error fetching insights or subtypes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, []);

  const renderContent = () => {
    if (loading || !insights) {
      return (
        <Box
          sx={{
            minHeight: 260,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <CircularProgress size={30} sx={{ color: "#2f63ad" }} />
          <Typography sx={{ color: "#7a8696", fontSize: "0.92rem" }}>
            Loading insights...
          </Typography>
        </Box>
      );
    }

    switch (selectedWidget) {
      case "subtypes":
        return (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                ...sectionTitleSx,
                fontSize: "1rem",
                mb: 2,
              }}
            >
              Top Work Order Subtypes
            </Typography>

            <Stack spacing={1.2}>
              {insights.topSubtypes.map((item, idx) => {
                const subtype = subtypeMap[item.workorder_subtype_id];
                const colors = [
                  "#2f63ad",
                  "#4f8df7",
                  "#26a69a",
                  "#fb8c00",
                  "#7e57c2",
                ];
                const badgeColor = colors[idx % colors.length];

                return (
                  <Box
                    key={idx}
                    sx={{
                      borderRadius: "14px",
                      border: "1px solid #eef2f7",
                      backgroundColor: "#fbfcfe",
                      px: 1.5,
                      py: 1.25,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: badgeColor,
                          mr: 1.25,
                          mt: "7px",
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#223142",
                            lineHeight: 1.35,
                          }}
                        >
                          {subtype?.name || "Unknown"}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: "#7a8696", ml: 1 }}
                          >
                            ({subtype?.type || "Type N/A"})
                          </Typography>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ color: "#5f6b7a", mt: 0.4 }}
                        >
                          Work Orders: <strong>{item.count}</strong>
                        </Typography>
                      </Box>
                    </Box>

                    {idx < insights.topSubtypes.length - 1 && (
                      <Divider sx={{ mt: 1.25, display: { xs: "none", sm: "block" } }} />
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        );

      case "weekly":
        if (!insights.weeklyByType?.length) {
          return (
            <Typography sx={{ color: "#7a8696", fontSize: "0.92rem" }}>
              No type data available for this month.
            </Typography>
          );
        }

        const colors = {
          Plumbing: "#26a69a",
          Electrical: "#4f8df7",
          HVAC: "#ef5350",
          Maintenance: "#7e57c2",
          Landscaping: "#fb8c00",
        };

        const types = Object.keys(insights.weeklyByType[0]).filter(
          (k) => k !== "week"
        );

        return (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                ...sectionTitleSx,
                fontSize: "1rem",
                mb: 0.5,
              }}
            >
              Work Order Volume by Type
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "#7a8696",
                display: "block",
                mb: 2,
              }}
            >
              Grouped by week this month
            </Typography>

            <Box
              sx={{
                height: 270,
                borderRadius: "14px",
                backgroundColor: "#fbfcfe",
                border: "1px solid #eef2f7",
                p: 1.5,
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={insights.weeklyByType.map((d) => ({
                    ...d,
                    label: `Week ${d.week}`,
                  }))}
                  margin={{ top: 10, right: 16, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: "#5f6b7a", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "#5f6b7a", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip />
                  {types.map((type, idx) => (
                    <Bar
                      key={type}
                      dataKey={type}
                      name={type}
                      fill={colors[type] || `hsl(${(idx * 60) % 360}, 70%, 50%)`}
                      radius={[6, 6, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        );

      case "resolution":
        const { thisMonth, lastMonth } = insights.averageResolutionTime || {};

        const resolutionCards = [
          {
            label: "Avg. Resolution Time - This Month",
            value: thisMonth != null ? `${thisMonth.toFixed(1)} days` : "No data",
            color: "#2f63ad",
            bg: "rgba(47,99,173,0.08)",
          },
          {
            label: "Avg. Resolution Time - Last Month",
            value: lastMonth != null ? `${lastMonth.toFixed(1)} days` : "No data",
            color: "#26a69a",
            bg: "rgba(38,166,154,0.10)",
          },
        ];

        const trendMessage =
          thisMonth != null &&
          lastMonth != null &&
          (thisMonth > lastMonth ? (
            <Typography
              variant="caption"
              sx={{
                color: "#d97706",
                mt: 1.25,
                display: "block",
                fontWeight: 600,
              }}
            >
              Resolution time has increased compared to last month.
            </Typography>
          ) : thisMonth < lastMonth ? (
            <Typography
              variant="caption"
              sx={{
                color: "#26a69a",
                mt: 1.25,
                display: "block",
                fontWeight: 600,
              }}
            >
              Improved resolution time compared to last month.
            </Typography>
          ) : (
            <Typography
              variant="caption"
              sx={{
                color: "#7a8696",
                mt: 1.25,
                display: "block",
                fontWeight: 600,
              }}
            >
              Resolution time is unchanged from last month.
            </Typography>
          ));

        return (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                ...sectionTitleSx,
                fontSize: "1rem",
                mb: 2,
              }}
            >
              Average Resolution Time
            </Typography>

            <Stack spacing={2}>
              {resolutionCards.map((card, idx) => (
                <Box
                  key={idx}
                  sx={{
                    borderRadius: "14px",
                    p: 2,
                    backgroundColor: card.bg,
                    border: "1px solid rgba(47,99,173,0.08)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: card.color,
                      letterSpacing: "0.2px",
                    }}
                  >
                    {card.label}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      mt: 0.5,
                      fontWeight: 800,
                      color: "#223142",
                    }}
                  >
                    {card.value}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {trendMessage}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ mt: 5, px: { xs: 2, sm: 3 }, py: 3 }}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={5} sx={{ display: "flex" }}>
          <Card sx={panelCardSx}>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    background: "rgba(47,99,173,0.10)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Insights sx={{ fontSize: 24, color: "#2f63ad" }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    Work Order Insights
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#7a8696" }}>
                    Summary widgets and trends
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 2.5, overflowX: "auto" }}>
                <ButtonGroup
                  variant="text"
                  sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    gap: 1,
                    minWidth: "max-content",
                  }}
                >
                  <Button
                    onClick={() => setSelectedWidget("subtypes")}
                    sx={buttonSx(selectedWidget === "subtypes")}
                  >
                    Subtypes
                  </Button>
                  <Button
                    onClick={() => setSelectedWidget("weekly")}
                    sx={buttonSx(selectedWidget === "weekly")}
                  >
                    Weekly
                  </Button>
                  <Button
                    onClick={() => setSelectedWidget("resolution")}
                    sx={buttonSx(selectedWidget === "resolution")}
                  >
                    Resolution
                  </Button>
                </ButtonGroup>
              </Box>

              {renderContent()}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7} sx={{ display: "flex" }}>
          <Card sx={panelCardSx}>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    background: "rgba(47,99,173,0.10)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Schedule sx={{ fontSize: 22, color: "#2f63ad" }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    Calendar
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#7a8696" }}>
                    Schedule and upcoming activity
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  borderRadius: "14px",
                  backgroundColor: "#fbfcfe",
                  border: "1px solid #eef2f7",
                  p: 1.5,
                }}
              >
                <Calendar defaultView="month" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InsightsWithCalendar;